import {
  CreditCard,
  Cloud,
  Globe,
  Search,
  Mail,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Badge } from '@/components/badge';
import { useIntegrationsStatus } from '@/lib/integrations';
import { SkeletonList } from '@/components/skeleton';

export function IntegrationsTab() {
  const { data, isLoading } = useIntegrationsStatus();

  if (isLoading || !data) {
    return (
      <Card>
        <CardBody>
          <SkeletonList rows={4} />
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Status reflects whether credentials are configured. The app doesn't dial out from this page —
        check the relevant dashboard if you suspect a connectivity issue.
      </p>

      <IntegrationCard
        icon={CreditCard}
        name="Stripe"
        description="Accepts deposits on approvables and powers subscriptions billing."
        configured={data.stripe.configured}
        details={[
          data.stripe.mode ? `Mode: ${data.stripe.mode}` : null,
          data.stripe.webhook_signing_secret ? 'Webhook signing secret set' : 'No webhook secret',
        ].filter(Boolean) as string[]}
        warning={data.stripe.configured && !data.stripe.webhook_signing_secret}
      />

      <IntegrationCard
        icon={Mail}
        name="Resend (email)"
        description="Sends approvable invitations, invoices, and admin invitations."
        configured={data.resend.configured}
        details={[
          `Mailer: ${data.resend.mailer}`,
          data.resend.from_address
            ? `From: ${data.resend.from_name ? `${data.resend.from_name} <${data.resend.from_address}>` : data.resend.from_address}`
            : null,
        ].filter(Boolean) as string[]}
      />

      <IntegrationCard
        icon={Search}
        name="Typesense"
        description="Backs the ⌘K search across clients, contacts, projects, and approvables."
        configured={data.typesense.configured}
        details={[data.typesense.driver ? `Scout driver: ${data.typesense.driver}` : null].filter(
          Boolean,
        ) as string[]}
      />

      <IntegrationCard
        icon={Cloud}
        name="Cloudflare R2"
        description="Object storage for documents, approvables, and asset photos."
        configured={data.r2.configured}
        details={[
          data.r2.disk ? `Disk: ${data.r2.disk}` : null,
          data.r2.bucket ? `Bucket: ${data.r2.bucket}` : null,
        ].filter(Boolean) as string[]}
      />

      <MarketingSiteCard />
    </div>
  );
}

function MarketingSiteCard() {
  return (
    <Card>
      <CardHeader className="flex items-start gap-3">
        <div className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Globe className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">Mehrutherm.com</h3>
            <Badge tone="success">
              <CheckCircle2 className="mr-1 size-3" /> Wired
            </Badge>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Public endpoints the marketing site posts to. Inquiries land as Leads with source =
            web_form and trigger an in-app notification.
          </p>
        </div>
      </CardHeader>
      <CardBody className="space-y-3 text-xs">
        <div>
          <div className="font-medium text-foreground">Routes</div>
          <ul className="mt-1 space-y-0.5 text-muted-foreground">
            <li>
              <code className="rounded bg-muted px-1 py-0.5">POST /api/website-audit</code> — Free
              website audit form
            </li>
            <li>
              <code className="rounded bg-muted px-1 py-0.5">POST /api/contact</code> — Quote /
              contact form
            </li>
          </ul>
        </div>
        <div>
          <div className="font-medium text-foreground">Security</div>
          <ul className="mt-1 space-y-0.5 text-muted-foreground">
            <li>· CORS allows https://mehrutherm.com and https://www.mehrutherm.com</li>
            <li>
              · Honeypot: a hidden <code className="rounded bg-muted px-1 py-0.5">company</code>{' '}
              field. Non-empty submissions are silently dropped + return 200.
            </li>
            <li>· Rate limit: 10 requests/min per IP.</li>
            <li>· No API token — the marketing site is static, anything bearer-ish would leak.</li>
          </ul>
        </div>
      </CardBody>
    </Card>
  );
}

function IntegrationCard({
  icon: Icon,
  name,
  description,
  configured,
  details,
  warning = false,
}: {
  icon: typeof CreditCard;
  name: string;
  description: string;
  configured: boolean;
  details: string[];
  warning?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex items-start gap-3">
        <div className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Icon className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">{name}</h3>
            {configured ? (
              warning ? (
                <Badge tone="warning">
                  <AlertCircle className="mr-1 size-3" /> Partial
                </Badge>
              ) : (
                <Badge tone="success">
                  <CheckCircle2 className="mr-1 size-3" /> Connected
                </Badge>
              )
            ) : (
              <Badge tone="neutral">
                <XCircle className="mr-1 size-3" /> Not configured
              </Badge>
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
        </div>
      </CardHeader>
      {details.length > 0 ? (
        <CardBody className="text-xs text-muted-foreground">
          <ul className="grid grid-cols-1 gap-1">
            {details.map((d, i) => (
              <li key={i}>· {d}</li>
            ))}
          </ul>
        </CardBody>
      ) : null}
    </Card>
  );
}
